import { useEffect, useRef } from 'react';
import type { Bot } from '../context';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  bots: Bot[];
  currentBotId: string | null;
  onSelectBot: (botId: string) => void;
}

/**
 * Modal component for selecting a project/bot.
 *
 * Displays a list of all available bots with their icons and names.
 * Features large icons and bold typography for visual impact.
 * Clicking a bot selects it and closes the modal.
 */
export function ProjectModal({
  isOpen,
  onClose,
  bots,
  currentBotId,
  onSelectBot,
}: ProjectModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      // Delay adding the listener to prevent immediate closing
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBotSelect = (botId: string) => {
    onSelectBot(botId);
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerIcon}>
              <span style={styles.headerIconEmoji}>🔄</span>
            </div>
            <div>
              <h2 style={styles.title}>Switch Project</h2>
              <p style={styles.subtitle}>Select a project to work with</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div style={styles.botList}>
          {bots.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📦</div>
              <div style={styles.emptyTitle}>No projects available</div>
              <div style={styles.emptyDescription}>
                Create your first project to get started
              </div>
            </div>
          ) : (
            bots.map((bot) => {
              const isSelected = bot.id === currentBotId;
              return (
                <button
                  key={bot.id}
                  onClick={() => handleBotSelect(bot.id)}
                  style={{
                    ...styles.botCard,
                    ...(isSelected ? styles.botCardSelected : {}),
                  }}
                >
                  <span
                    style={{
                      ...styles.botIcon,
                      backgroundColor: bot.color || '#3b82f6',
                    }}
                  >
                    {bot.icon || '🤖'}
                  </span>
                  <div style={styles.botInfo}>
                    <span style={styles.botName}>{bot.name}</span>
                    <span style={styles.botSlug}>{bot.slug}</span>
                  </div>
                  {isSelected && (
                    <span style={styles.checkmark}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#22c55e" />
                        <path
                          d="M6 10L9 13L14 7"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 20px',
    borderBottom: '1px solid #f1f5f9',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    backgroundColor: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIconEmoji: {
    fontSize: '28px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '4px 0 0 0',
  },
  closeButton: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: '8px',
    lineHeight: 1,
    transition: 'all 0.15s ease',
  },
  botList: {
    padding: '16px',
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  emptyDescription: {
    fontSize: '14px',
    color: '#64748b',
  },
  botCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    padding: '16px',
    backgroundColor: '#ffffff',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
  },
  botCardSelected: {
    backgroundColor: '#f8fafc',
    borderColor: '#22c55e',
  },
  botIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    fontSize: '26px',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  botInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0,
  },
  botName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '-0.01em',
  },
  botSlug: {
    fontSize: '13px',
    color: '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  checkmark: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default ProjectModal;
